import { Loader } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";

import classes from "./LoadAhead.module.css";

/** A paged list's next page: whether there is one, whether it is on its way, and how to ask. */
export interface MorePages {
  has: boolean;
  loading: boolean;
  load: () => void;
}

/** An infinite query's next page as `MorePages` (TanStack's `useInfiniteQuery` result). */
export function morePages(pages: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}): MorePages {
  return { has: pages.hasNextPage, loading: pages.isFetchingNextPage, load: () => void pages.fetchNextPage() };
}

/** Whether `target` is within `ahead` screens of the view now, read from the layout itself:
 *  after a page arrives, the observer's last word may be from before the list grew. */
function withinReach(target: HTMLElement, root: HTMLElement | undefined, ahead: number): boolean {
  const view = root?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
  return target.getBoundingClientRect().top <= view.bottom + (view.bottom - view.top) * ahead;
}

export interface LoadAheadProps {
  more: MorePages;
  /** How many items the list holds now: each page that arrives asks again whether the end is
   *  still near (live's `withEmptySpaceCheck`), as no new crossing may tell. */
  loaded: number;
  /**
   * The element that scrolls the list; `null` until it mounts, `undefined` for the window (live's
   * lists pass `root: null`: with no margin, the end shows in its own scroller as in the window).
   */
  root?: HTMLElement | null;
  /**
   * How many screens before the end the next page is asked for: none, live's default
   * (`rootMargin: '0px'`); the library's three (`'0px 0px 300% 0px'`), which needs its own
   * scroller as `root`, since a scroller clips the window's margin.
   */
  ahead?: number;
  /** The spinner's name for a screen reader ("Loading more files"). */
  label: string;
  /**
   * No spinner: the list shows its own placeholders while the page comes, and its end is a 16 px
   * mark (the editor's Images and Videos, as live's editor: skeleton tiles, an `h-4` sentinel).
   */
  quiet?: boolean;
}

/**
 * Live's `InfiniteScroll` (`components/infinite-scroll`): the end of a paged list, after its last
 * item and inside its scroll. While there is a next page it shows live's spinner (`quiet`: none,
 * the list's own placeholders instead), and once it is
 * in view (or within `ahead` screens of it) it asks for the page; it asks again for each page that still
 * leaves it there, so a first page too short to scroll fills the view (live's
 * `withEmptySpaceCheck`). No button and no words at the end: the box stays, empty. A keyboard
 * reaches it as the cards scroll into view, so it reads on as a mouse does.
 */
export function LoadAhead({ more, loaded, root, label, ahead = 0, quiet = false }: LoadAheadProps) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  // Each observer's own answer: one from before a new list (or a remount) never asks for its page.
  const watch = useMemo(() => ({ target, root, has: more.has, ahead }), [target, root, more.has, ahead]);
  const [seen, setSeen] = useState<{ watch: typeof watch; near: boolean } | null>(null);
  // This observer's latest answer: a new object for each, so each is weighed again.
  const answer = seen !== null && seen.watch === watch ? seen : null;

  useEffect(() => {
    if (watch.target === null || watch.root === null || !watch.has) return;
    const observer = new IntersectionObserver(
      (entries) => setSeen({ watch, near: entries.some((entry) => entry.isIntersecting) }),
      { root: watch.root ?? null, rootMargin: `0px 0px ${watch.ahead * 100}% 0px`, threshold: 0 },
    );
    observer.observe(watch.target);
    return () => observer.disconnect();
  }, [watch]);

  // Callers pass `load` as a new arrow each render: asking again on that alone would ask twice
  // for one page (live keeps it in a ref for the same reason).
  const load = useRef(more.load);
  useEffect(() => {
    load.current = more.load;
  });
  const { has, loading } = more;
  useEffect(() => {
    if (answer === null || !answer.near || !has || loading || target === null || root === null) return;
    // A page that arrived (`loaded`) may leave the end near with no new crossing to say so, or
    // move it away before the observer says: the layout decides.
    if (withinReach(target, root, ahead)) {
      load.current();
      return;
    }
    // The page moved the end just past reach, yet the observer's word is still "near": it measured
    // it a pixel apart, or a scroll brought it back before it looked. Its word then never changes,
    // so it never speaks again, and the list would stop for good (pro-uploads on a busy runner,
    // 192 of 301): until it does, each scroll asks the layout, and the first within reach loads.
    const scroller = root ?? window;
    let asked = false;
    const scrolled = () => {
      if (asked || !withinReach(target, root, ahead)) return;
      asked = true;
      load.current();
    };
    scroller.addEventListener("scroll", scrolled, { passive: true });
    return () => scroller.removeEventListener("scroll", scrolled);
  }, [answer, has, loading, loaded, target, root, ahead]);

  if (quiet) return <div ref={setTarget} className={classes.mark} aria-hidden />;
  return (
    <div ref={setTarget} className={classes.target}>
      {more.has ? <Loader aria-label={label} /> : null}
    </div>
  );
}
